import { removeCookieSecureData } from 'stores/user/helpers/remove-cookie-secure-data';
import { checkIsAuthorized } from 'stores/user/helpers/check-if-authorized';
import axios, { type AxiosRequestConfig, type AxiosPromise } from 'axios';
import { USER_LOCAL_STORAGE_NAME } from 'constants/local-storage';
import { getAccessToken } from 'utils/get-access-token';
import { BASE_URL } from 'constants/services';

const axiosInstance = axios.create({ baseURL: BASE_URL, method: 'POST' });

const axiosExport = <T = void>(config: AxiosRequestConfig, withToken = true) => {
    const request: AxiosPromise<T> = axiosInstance({
        ...config,
        headers: {
            ...(withToken && {
                Authorization: `Bearer ${getAccessToken()}`
            }),
            ...config.headers
        }
    });

    return request as any as Promise<T>;
};

axiosInstance.interceptors.response.use(
    config => config.data,
    async error => {
        const originalRequest = error.config;
        const status = error.response ? error.response.status : null;

        if (status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const { isAuthorized, accessToken } = await checkIsAuthorized();

                if (!isAuthorized) {
                    removeCookieSecureData();
                    localStorage.removeItem(USER_LOCAL_STORAGE_NAME);
                    return Promise.reject(new Error('Unauthorized'));
                }

                originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

                return axiosExport(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosExport;
